<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import KanbanAPI from '../../../../api/kanban';
import agents from '../../../../api/agents';
import Button from '../../../../components-next/button/Button.vue';

const store = useStore();
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();

const selectedItems = ref([]);
const selectedAgent = ref('');
const assignMode = ref('replace'); // 'replace' ou 'add'
const kanbanItems = ref([]);
const agentsList = ref([]);
const loadingAgents = ref(false);

// Buscar itens do Kanban
const fetchKanbanItems = async () => {
  try {
    if (!selectedFunnel.value?.id) return;

    if (props.items.length === 0) {
      const response = await KanbanAPI.getItems(selectedFunnel.value.id);
      const itemsData = response.data?.items || [];
      kanbanItems.value = itemsData;
    } else {
      kanbanItems.value = props.items;
    }
  } catch (error) {
    console.error('[BulkAssignAgentModal] Erro ao carregar itens:', error);
  }
};

// Buscar agentes
const fetchAgents = async () => {
  try {
    loadingAgents.value = true;
    const { data } = await agents.get();
    agentsList.value = data || [];
  } catch (error) {
    console.error('[BulkAssignAgentModal] Erro ao carregar agentes:', error);
    agentsList.value = [];
  } finally {
    loadingAgents.value = false;
  }
};

const toggleItem = itemId => {
  const index = selectedItems.value.indexOf(itemId);
  if (index === -1) {
    selectedItems.value.push(itemId);
  } else {
    selectedItems.value.splice(index, 1);
  }
};

const toggleSelectAll = () => {
  const itemsToUse = kanbanItems.value.length > 0 ? kanbanItems.value : props.items;
  if (selectedItems.value.length === itemsToUse.length) {
    selectedItems.value = [];
  } else {
    selectedItems.value = itemsToUse.map(item => item.id);
  }
};

const isAllSelected = computed(() => {
  const itemsToUse = kanbanItems.value.length > 0 ? kanbanItems.value : props.items;
  return selectedItems.value.length === itemsToUse.length;
});

const handleConfirm = () => {
  if (selectedAgent.value && selectedItems.value.length > 0) {
    emit('confirm', {
      itemIds: selectedItems.value,
      agentId: selectedAgent.value,
      mode: assignMode.value,
    });
  }
};

onMounted(() => {
  fetchKanbanItems();
  fetchAgents();
});
</script>

<template>
  <div class="bulk-assign-agent-modal">
    <div class="p-6">
      <h3 class="text-lg font-medium mb-4">
        Atribuir Atendente em Massa
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
        Selecione os itens e o atendente que deseja atribuir.
      </p>

      <!-- Seleção de Agente -->
      <div class="agent-selector mb-4">
        <label class="block text-sm font-medium mb-2">
          Atendente
        </label>
        <select
          v-model="selectedAgent"
          class="w-full p-2 border rounded-lg bg-white dark:bg-slate-800"
        >
          <option value="" disabled>
            Selecione um atendente
          </option>
          <option
            v-for="agent in agentsList"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }} ({{ agent.email }})
          </option>
        </select>
      </div>

      <!-- Modo de Atribuição -->
      <div class="mode-selector mb-4">
        <label class="block text-sm font-medium mb-2">
          Modo de Atribuição
        </label>
        <div class="flex gap-4">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              v-model="assignMode"
              value="replace"
              class="cursor-pointer"
            />
            <span class="text-sm">Substituir atendentes existentes</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              v-model="assignMode"
              value="add"
              class="cursor-pointer"
            />
            <span class="text-sm">Adicionar aos existentes</span>
          </label>
        </div>
      </div>

      <!-- Lista de Itens -->
      <div class="items-list max-h-96 overflow-y-auto mb-4">
        <!-- Select All Header -->
        <div
          class="item-row flex items-center p-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer"
          @click="toggleSelectAll"
        >
          <input
            type="checkbox"
            :checked="isAllSelected"
            class="mr-3"
            @click.stop
          />
          <div class="flex-1">
            <h4 class="text-sm font-medium">
              {{ t('KANBAN.BULK_ACTIONS.SELECT_ALL') }}
            </h4>
          </div>
        </div>

        <!-- Individual Items -->
        <div
          v-for="item in kanbanItems.length > 0 ? kanbanItems : items"
          :key="item.id"
          class="item-row flex items-center p-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer"
          :class="{
            'bg-slate-50 dark:bg-slate-800': selectedItems.includes(item.id),
          }"
          @click="toggleItem(item.id)"
        >
          <input
            type="checkbox"
            :checked="selectedItems.includes(item.id)"
            class="mr-3"
            @click.stop
          />
          <div class="flex-1">
            <h4 class="text-sm font-medium">
              {{
                item.item_details?.title ||
                item.title ||
                t('KANBAN.ITEM_DETAILS.UNTITLED_ITEM')
              }}
            </h4>
            <div class="flex items-center gap-2 mt-1">
              <span
                v-if="item.assigned_agents && item.assigned_agents.length > 0"
                class="text-xs text-slate-500 dark:text-slate-400"
              >
                Atendentes: {{ item.assigned_agents.map(a => a.name).join(', ') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <Button variant="ghost" color="slate" size="sm" @click="$emit('close')">
          {{ t('KANBAN.BULK_ACTIONS.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          :disabled="!selectedAgent || !selectedItems.length"
          @click="handleConfirm"
        >
          Atribuir ({{ selectedItems.length }})
        </Button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.bulk-assign-agent-modal {
  width: 100%;
  max-width: 600px;
}

.items-list {
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small);
}

.item-row {
  border-bottom: 1px solid var(--color-border);
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }
}
</style>
